import type { QueryClient } from "@tanstack/react-query";

import { stipulationKeys, type StipulationListKeyArguments } from "./stipulationKeys";
import type { StipulationListView } from "./types";

/**
 * Patches every cached stipulation list for the given approval, letting the
 * caller compute the next list from the cached one and that list's key
 * arguments (e.g. `openOnly`).
 * @param {QueryClient} queryClient - The active query client.
 * @param {string} approvalId - Only lists whose key matches this approval are patched.
 * @param {(current: StipulationListView, keyArguments: StipulationListKeyArguments) => StipulationListView} patch - Computes the next list from the cached one.
 */
export function patchStipulationListsForApproval(
  queryClient: QueryClient,
  approvalId: string,
  patch: (current: StipulationListView, keyArguments: StipulationListKeyArguments) => StipulationListView
): void {
  // `approvalId` is a key segment, so this partial-matches only this
  // approval's cached lists instead of scanning every approval's.
  const queries = queryClient.getQueriesData<StipulationListView>({
    queryKey: stipulationKeys.listsForApproval(approvalId),
  });

  for (const [queryKey, data] of queries) {
    if (!data) {
      continue;
    }
    const openOnly = queryKey[3] as boolean | undefined;
    queryClient.setQueryData<StipulationListView>(queryKey, patch(data, { approvalId, openOnly }));
  }
}
